<template>
  <section class="admin-content">
    <Navbar />

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="container">
        <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
          <div class="d-flex align-items-center" style="gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-upload-cloud" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Claim Documents</h4>
              <small style="opacity: 0.8;">Upload supporting documents for this claim</small>
            </div>
          </div>
          <button class="btn btn-outline-light btn-sm" @click="gotoSingleClaim">
            <i class="fe fe-arrow-right mr-1"></i> Proceed to Claim
          </button>
        </div>
      </div>
    </div>

    <section style="margin-top: -32px;">
      <div class="container pb-5">
        <div class="row">

          <!-- ── Upload Card ── -->
          <div class="col-lg-5 mb-4">
            <div class="card shadow table-card h-100">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-upload" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white">Upload Documents</h6>
              </div>
              <div class="card-body px-4 py-4">

                <!-- Drop zone -->
                <div class="drop-zone mb-3"
                  :class="{ 'drop-zone--over': isDragging }"
                  @dragover.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false"
                  @drop.prevent="onDrop"
                  @click="$refs.fileInput.click()">
                  <i class="fe fe-file-plus" style="font-size: 2.2rem; color: #2563eb; opacity: 0.7;"></i>
                  <p class="mb-1 font-weight-bold mt-2" style="font-size: 0.9rem;">
                    Click or drag files here
                  </p>
                  <small class="text-muted">PDF, JPG, PNG — any number of files</small>
                  <input ref="fileInput" type="file" multiple class="d-none" @change="onFilesSelected" />
                </div>

                <!-- Upload queue -->
                <div v-if="uploadQueue.length" class="mb-3">
                  <div v-for="(item, index) in uploadQueue" :key="index"
                    class="d-flex align-items-center p-2 rounded mb-2"
                    :class="{
                      'bg-light':           item.status === 'pending',
                      'queue-item--uploading': item.status === 'uploading',
                      'queue-item--done':   item.status === 'done',
                      'queue-item--error':  item.status === 'error',
                    }">
                    <!-- Icon -->
                    <div class="mr-2" style="flex-shrink: 0;">
                      <span v-if="item.status === 'pending'"   class="text-muted"><i class="fe fe-file"></i></span>
                      <span v-if="item.status === 'uploading'" class="spinner-border spinner-border-sm text-primary"></span>
                      <span v-if="item.status === 'done'"      class="text-success"><i class="fe fe-check-circle"></i></span>
                      <span v-if="item.status === 'error'"     class="text-danger"><i class="fe fe-x-circle"></i></span>
                    </div>

                    <!-- File info -->
                    <div class="flex-grow-1 overflow-hidden">
                      <p class="mb-0 text-truncate" style="font-size: 0.82rem; font-weight: 600;">{{ item.file.name }}</p>
                      <div v-if="item.status === 'uploading'" class="progress mt-1" style="height: 3px;">
                        <div class="progress-bar bg-primary" :style="`width: ${item.progress}%`"></div>
                      </div>
                      <small v-else-if="item.status === 'done'"  class="text-success">Uploaded</small>
                      <small v-else-if="item.status === 'error'" class="text-danger">Failed — click Retry</small>
                      <small v-else class="text-muted">{{ formatSize(item.file.size) }}</small>
                    </div>

                    <!-- Remove (pending only) -->
                    <button v-if="item.status === 'pending'" type="button"
                      class="btn btn-sm btn-link text-muted p-0 ml-2"
                      @click="removeFromQueue(index)" style="flex-shrink: 0;">
                      <i class="fe fe-x"></i>
                    </button>
                  </div>
                </div>

                <div v-if="!uploadQueue.length" class="text-center text-muted py-3" style="font-size: 0.82rem;">
                  No files selected yet.
                </div>

                <!-- Actions -->
                <div class="d-flex" style="gap: 8px;">
                  <button class="btn btn-submit flex-grow-1"
                    :disabled="uploading || !pendingCount"
                    @click="startUpload">
                    <span v-if="uploading">
                      <span class="spinner-border spinner-border-sm mr-1"></span>
                      Uploading {{ doneCount + 1 }} / {{ uploadQueue.length }}…
                    </span>
                    <span v-else>
                      <i class="fe fe-upload-cloud mr-1"></i>
                      Upload {{ pendingCount || '' }} File{{ pendingCount !== 1 ? 's' : '' }}
                    </span>
                  </button>
                  <button v-if="uploadQueue.length" class="btn btn-outline-secondary btn-sm"
                    :disabled="uploading" @click="clearQueue" title="Clear list">
                    <i class="fe fe-trash-2"></i>
                  </button>
                </div>

              </div>
            </div>
          </div>

          <!-- ── Uploaded Documents Table ── -->
          <div class="col-lg-7 mb-4">
            <div class="card shadow table-card">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-file" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white">Uploaded Documents</h6>
                <span v-if="singleclaim.docs" class="badge badge-light ml-auto" style="border-radius: 20px;">
                  {{ singleclaim.docs.length }}
                </span>
              </div>
              <div class="table-responsive">
                <table class="table align-td-middle mb-0">
                  <thead class="table-head">
                    <tr>
                      <th class="table-th">#</th>
                      <th class="table-th">Name</th>
                      <th class="table-th">Uploaded By</th>
                      <th class="table-th">Date</th>
                      <th class="table-th"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(doc, index) in singleclaim.docs" :key="doc.id" class="table-row">
                      <td class="px-4 py-3">{{ index + 1 }}</td>
                      <td class="py-3">
                        <i class="fe fe-file-text text-muted mr-1"></i>
                        {{ doc.name }}
                      </td>
                      <td class="py-3" style="font-size: 0.85rem;">
                        {{ doc.user && doc.user.firstname }} {{ doc.user && doc.user.lastname }}
                      </td>
                      <td class="py-3" style="font-size: 0.85rem;">
                        {{ doc.created_at | moment("MMM Do YYYY") }}
                      </td>
                      <td class="py-3">
                        <div class="btn-group btn-group-sm">
                          <a :href="'https://claimify-api.hayokmedicare.ng/documents/' + doc.document" target="_blank">
                            <button class="btn btn-outline-secondary btn-sm">
                              <i class="fe fe-eye"></i>
                            </button>
                          </a>
                          <button class="btn btn-outline-danger btn-sm ml-1" @click="deleteDoc(doc)">
                            <i class="fe fe-trash-2"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!singleclaim.docs || !singleclaim.docs.length">
                      <td colspan="5" class="text-center py-5 text-muted">
                        <i class="fe fe-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                        <p class="mt-2 mb-0">No documents uploaded yet</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>

        <!-- ── Proceed footer ── -->
        <div class="d-flex justify-content-end">
          <button class="btn btn-submit" @click="gotoSingleClaim">
            Done — Proceed to Claim <i class="fe fe-arrow-right ml-1"></i>
          </button>
        </div>

      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </section>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: { Navbar, Loading },

  data() {
    return {
      user: null,
      singleclaim: { docs: [] },
      isLoading: false,
      fullPage: true,
      isDragging: false,
      uploading: false,
      uploadQueue: [],
    };
  },

  computed: {
    pendingCount() {
      return this.uploadQueue.filter((i) => i.status === "pending").length;
    },
    doneCount() {
      return this.uploadQueue.filter((i) => i.status === "done").length;
    },
  },

  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },

  created() {
    this.getSingleClaim();
  },

  methods: {
    getSingleClaim() {
      this.axios
        .get(`/api/v1/auth/detailedClaim/${this.$route.params.id}`)
        .then((response) => { this.singleclaim = response.data; })
        .catch((error) => { console.error(error); });
    },

    deleteDoc(doc) {
      if (confirm("Are you sure you want to delete this document permanently?")) {
        this.axios
          .delete(`/api/v1/auth/deleteClaimDocument/${doc.id}`)
          .then(() => {
            this.getSingleClaim();
            this.$toasted.info("Deleted successfully", { position: "top-center", duration: 3000 });
          })
          .catch((error) => { console.error(error); });
      }
    },

    gotoSingleClaim() {
      this.$router.push(`/claim/${this.$route.params.id}`);
    },

    onFilesSelected(event) {
      this.addFiles(event.target.files);
      event.target.value = "";
    },

    onDrop(event) {
      this.isDragging = false;
      this.addFiles(event.dataTransfer.files);
    },

    addFiles(fileList) {
      for (const file of fileList) {
        this.uploadQueue.push({ file, status: "pending", progress: 0 });
      }
    },

    removeFromQueue(index) {
      this.uploadQueue.splice(index, 1);
    },

    clearQueue() {
      this.uploadQueue = this.uploadQueue.filter((i) => i.status !== "pending");
    },

    formatSize(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },

    async startUpload() {
      this.uploading = true;
      for (const item of this.uploadQueue) {
        if (item.status !== "pending") continue;
        await this.uploadOne(item);
      }
      this.uploading = false;
      this.getSingleClaim();
      const failed = this.uploadQueue.filter((i) => i.status === "error").length;
      const done = this.uploadQueue.filter((i) => i.status === "done").length;
      if (done) {
        this.$toasted.success(
          `${done} document${done !== 1 ? "s" : ""} uploaded successfully${failed ? `, ${failed} failed` : ""}!`,
          { position: "top-center", duration: 3000 }
        );
      }
    },

    uploadOne(item) {
      item.status = "uploading";
      item.progress = 0;

      const formData = new FormData();
      formData.append("claim_document", item.file);
      formData.append("claim_id", this.$route.params.id);
      formData.append("doc_name", item.file.name);

      return this.axios
        .post("/api/v1/auth/uploadClaimDocument", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            item.progress = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          },
        })
        .then(() => { item.status = "done"; item.progress = 100; })
        .catch((error) => {
          console.error(error);
          item.status = "error";
        });
    },
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  padding-bottom: 40px;
}

.header-icon-wrap {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.table-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.section-number {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.table-th {
  font-size: 0.73rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
  border-bottom: 2px solid #e2e8f0;
  padding: 12px 16px;
}

.table-row:hover { background: #f8fafc; }

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none;
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
  padding: 10px 20px;
}

.btn-submit:hover { opacity: 0.9; color: #fff; }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

.drop-zone {
  border: 2px dashed #c7d2e8;
  border-radius: 12px;
  background: #f7f9ff;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
.drop-zone:hover,
.drop-zone--over {
  border-color: #2563eb;
  background: #eef2ff;
}
.queue-item--uploading {
  background: #f0f4ff;
  border: 1px solid #d0d9ee;
  border-radius: 6px;
}
.queue-item--done {
  background: #f0fff4;
  border: 1px solid #a8e6b8;
  border-radius: 6px;
}
.queue-item--error {
  background: #fff5f5;
  border: 1px solid #fca5a5;
  border-radius: 6px;
}
</style>
